import {
  OrganizationResponseForAdmins,
  OrganizationsService,
  OrgEmailPrefsService,
  OrgSMSPrefResponse,
  OrgSMSPrefUpdate,
  OrgSmsPrefsService,
} from "client/openapi";
import {
  COMPACT_INPUT,
  COMPACT_NUMBER,
  FieldError,
} from "components/Inputs/compact";
import { Switch } from "components/Switch";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  LABEL,
  SECTION,
  SECTION_TEXT,
  SECTION_TITLE,
  wholeNumberError,
} from "./settingsStyles";

/** SMS and email preferences have the same fields. */
type Prefs = OrgSMSPrefResponse;
type Channel = "sms" | "email";
const CHANNELS: Channel[] = ["sms", "email"];
const CHANNEL_NAMES: Record<Channel, string> = { sms: "SMS", email: "Email" };

/**
 * The messages an admin chooses. Who gets each is what the senders do: a
 * message to a student is copied to their parents, by email (bcc,
 * sendgrid/email.py) and by SMS (twilio/sms.py) alike. The reminder comes
 * last, above its timing. MEETING_UPDATED is left out: nothing sends it.
 */
const TYPES = [
  {
    key: "MEETING_REQUEST",
    name: "Meeting request",
    about:
      "To the tutor who has to accept or decline, and to your admins, when someone asks for a meeting.",
  },
  {
    key: "MEETING_SCHEDULED",
    name: "Meeting booked",
    about:
      "To the students, their parents and the tutors, when someone is booked into a meeting.",
  },
  {
    key: "MEETING_RESCHEDULED",
    name: "Meeting moved",
    about:
      "To the students, their parents and the tutors, when a meeting's time changes.",
  },
  {
    key: "MEETING_CANCELLED",
    name: "Meeting cancelled",
    about:
      "To the students, their parents and the tutors, when a meeting is cancelled or someone is taken off it.",
  },
  {
    key: "MEETING_ATTENDANCE",
    name: "Student marked absent",
    about:
      "To the student and their parents, after a meeting the student missed.",
  },
  {
    key: "NEW_SIGNUP",
    name: "New sign-up",
    about:
      "To your organization's contact, when a new family books through your booking page.",
  },
  {
    key: "TASK_ASSIGNMENT",
    name: "Task assigned",
    about:
      "To whoever is given or taken off a task. A student's parents get a copy.",
  },
  {
    key: "MEETING_REMINDER",
    name: "Meeting reminder",
    about:
      "To the students and their parents before each meeting. Set the timing below.",
  },
] as const;
type Flag = (typeof TYPES)[number]["key"];

const MIN_HOURS = 1;
const MAX_HOURS = 168;

/**
 * One channel as edited: the hours stay text until they are valid. SMS also
 * has the weekly pending-approval text (its email is an org setting).
 */
type Draft = {
  flags: Record<Flag, boolean>;
  hours: string;
  message: string;
  pendingApprovalTexts?: boolean;
};

function toDraft(prefs: Prefs, channel: Channel): Draft {
  const flags = {} as Record<Flag, boolean>;
  TYPES.forEach(({ key }) => (flags[key] = !!prefs[key]));
  return {
    flags,
    hours: String(prefs.reminder_hours_before ?? 24),
    message: prefs.custom_reminder_message ?? "",
    ...(channel === "sms" && {
      pendingApprovalTexts: !!prefs.PENDING_APPROVAL_REMINDER,
    }),
  };
}

/** Reminder hours only matter, and are only checked, while reminders are on. */
function hoursError(draft: Draft) {
  return draft.flags.MEETING_REMINDER
    ? wholeNumberError(draft.hours, MIN_HOURS, MAX_HOURS)
    : undefined;
}

/** What differs from the saved preferences, as a PATCH body. */
function changes(draft: Draft, saved: Prefs): OrgSMSPrefUpdate {
  const update: OrgSMSPrefUpdate = {};
  TYPES.forEach(({ key }) => {
    if (draft.flags[key] !== !!saved[key]) update[key] = draft.flags[key];
  });
  if (draft.flags.MEETING_REMINDER) {
    const hours = Number(draft.hours);
    if (hours !== saved.reminder_hours_before) {
      update.reminder_hours_before = hours;
    }
    const message = draft.message.trim() || null;
    if (message !== (saved.custom_reminder_message ?? null)) {
      update.custom_reminder_message = message;
    }
  }
  if (
    draft.pendingApprovalTexts !== undefined &&
    draft.pendingApprovalTexts !== !!saved.PENDING_APPROVAL_REMINDER
  ) {
    update.PENDING_APPROVAL_REMINDER = draft.pendingApprovalTexts;
  }
  return update;
}

const hasChanges = (update: OrgSMSPrefUpdate) => Object.keys(update).length > 0;

/**
 * The draft after `sent` was stored: a field that still shows what was sent
 * takes the stored value (the trimmed note, say); anything edited since
 * stays as the user left it.
 */
function settle(
  draft: Draft,
  sent: OrgSMSPrefUpdate,
  stored: Prefs,
  channel: Channel
): Draft {
  const fresh = toDraft(stored, channel);
  const flags = { ...draft.flags };
  TYPES.forEach(({ key }) => {
    if (key in sent && draft.flags[key] === sent[key]) {
      flags[key] = fresh.flags[key];
    }
  });
  const next = { ...draft, flags };
  if (
    "reminder_hours_before" in sent &&
    Number(draft.hours) === sent.reminder_hours_before
  ) {
    next.hours = fresh.hours;
  }
  if (
    "custom_reminder_message" in sent &&
    (draft.message.trim() || null) === sent.custom_reminder_message
  ) {
    next.message = fresh.message;
  }
  if (
    "PENDING_APPROVAL_REMINDER" in sent &&
    draft.pendingApprovalTexts === sent.PENDING_APPROVAL_REMINDER
  ) {
    next.pendingApprovalTexts = fresh.pendingApprovalTexts;
  }
  return next;
}

const SEND: Record<
  Channel,
  (args: { prefId: number; requestBody: OrgSMSPrefUpdate }) => Promise<Prefs>
> = {
  sms: (args) => OrgSmsPrefsService.updateOrgSmsPrefs(args),
  email: (args) => OrgEmailPrefsService.updateOrgEmailPrefs(args),
};

/**
 * The organization's SMS and email preferences, for the settings dialog's
 * Save and Cancel. They load the first time `wanted` is true, from the
 * organization (admins only).
 */
export function useNotificationPrefs(orgId: number, wanted: boolean) {
  const [saved, setSaved] = useState<Record<Channel, Prefs>>();
  const [drafts, setDrafts] = useState<Record<Channel, Draft>>();
  const [loadError, setLoadError] = useState<string>();
  const requested = useRef(false);

  useEffect(() => {
    if (!wanted || requested.current) return;
    requested.current = true;
    setLoadError(undefined);
    OrganizationsService.getOrganization({ orgId })
      .then((org) => {
        const { sms_prefs, email_prefs } = org as OrganizationResponseForAdmins;
        const sms = sms_prefs?.[0];
        const email = email_prefs?.[0];
        if (!sms || !email) {
          setLoadError("Notifications aren't set up for this organization.");
          return;
        }
        setSaved({ sms, email });
        setDrafts({ sms: toDraft(sms, "sms"), email: toDraft(email, "email") });
      })
      .catch(() => {
        // Opening the tab again tries again.
        requested.current = false;
        setLoadError("The notification settings could not be loaded.");
      });
  }, [orgId, wanted]);

  const updates =
    saved && drafts
      ? {
          sms: changes(drafts.sms, saved.sms),
          email: changes(drafts.email, saved.email),
        }
      : undefined;

  const edit = (channel: Channel, change: Partial<Draft>) =>
    setDrafts(
      (current) =>
        current && { ...current, [channel]: { ...current[channel], ...change } }
    );

  return {
    drafts,
    loadError,
    changed:
      !!updates && (hasChanges(updates.sms) || hasChanges(updates.email)),
    invalid:
      !!drafts && CHANNELS.some((channel) => hoursError(drafts[channel])),
    setFlag: (channel: Channel, flag: Flag, on: boolean) =>
      drafts &&
      edit(channel, { flags: { ...drafts[channel].flags, [flag]: on } }),
    setHours: (channel: Channel, hours: string) => edit(channel, { hours }),
    setMessage: (channel: Channel, message: string) =>
      edit(channel, { message }),
    setPendingApprovalTexts: (on: boolean) =>
      edit("sms", { pendingApprovalTexts: on }),
    /**
     * Sends what changed, each channel on its own. `saved` follows the server
     * for every channel that was stored, even if the other failed; drafts stay
     * the user's, so edits made during the save survive it. Rejects with the
     * first failure.
     */
    save: async () => {
      if (!saved || !updates) return;
      const sending = CHANNELS.filter((channel) =>
        hasChanges(updates[channel])
      );
      const results = await Promise.allSettled(
        sending.map((channel) =>
          SEND[channel]({
            prefId: saved[channel].id,
            requestBody: updates[channel],
          })
        )
      );
      const stored: Partial<Record<Channel, Prefs>> = {};
      results.forEach((result, i) => {
        if (result.status === "fulfilled") stored[sending[i]] = result.value;
      });
      setSaved((current) => current && { ...current, ...stored });
      setDrafts((current) => {
        if (!current) return current;
        const next = { ...current };
        Object.entries(stored).forEach(([channel, prefs]) => {
          const c = channel as Channel;
          next[c] = settle(current[c], updates[c], prefs as Prefs, c);
        });
        return next;
      });
      const failed = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (failed) throw failed.reason;
    },
    reset: () =>
      saved &&
      setDrafts({
        sms: toDraft(saved.sms, "sms"),
        email: toDraft(saved.email, "email"),
      }),
  };
}

export type NotificationPrefs = ReturnType<typeof useNotificationPrefs>;

/** A switch alone in a table cell, named for screen readers. */
function CellSwitch({
  name,
  label,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="inline-flex align-middle">
      <Switch
        name={name}
        label={label}
        checked={checked}
        onCheckedChange={onChange}
      >
        {null}
      </Switch>
    </div>
  );
}

function MessageCell({ name, about }: { name: string; about: string }) {
  return (
    <td className="py-2.5 pr-3">
      <span className="block text-sm text-neutral-900">{name}</span>
      <span className="block text-xs text-gray-600">{about}</span>
    </td>
  );
}

/** A message's row: what it is, then its SMS and Email switches. */
function FlagRow({
  type: { key, name, about },
  drafts,
  prefs,
}: {
  type: (typeof TYPES)[number];
  drafts: Record<Channel, Draft>;
  prefs: NotificationPrefs;
}) {
  return (
    <tr>
      <MessageCell name={name} about={about} />
      {CHANNELS.map((channel) => (
        <td key={channel} className="py-2.5 text-center">
          <CellSwitch
            name={`${channel}-${key}`}
            label={`${name} by ${CHANNEL_NAMES[channel]}`}
            checked={drafts[channel].flags[key]}
            onChange={(on) => prefs.setFlag(channel, key, on)}
          />
        </td>
      ))}
    </tr>
  );
}

/** One line per channel whose reminder is on: hours before, and a note. */
function ReminderTiming({
  channels,
  drafts,
  prefs,
}: {
  channels: Channel[];
  drafts: Record<Channel, Draft>;
  prefs: NotificationPrefs;
}) {
  return (
    <div className="mb-2.5 grid grid-cols-[3rem_auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 rounded-md bg-gray-50 px-3 py-2">
      {channels.map((channel) => {
        const draft = drafts[channel];
        const error = hoursError(draft);
        const hoursId = `${channel}-reminder-hours`;
        const name = CHANNEL_NAMES[channel];
        return (
          <Fragment key={channel}>
            <span className="text-xs font-semibold text-gray-600">{name}</span>
            <span className="flex items-center gap-2 whitespace-nowrap text-sm">
              <input
                id={hoursId}
                type="number"
                inputMode="numeric"
                min={MIN_HOURS}
                max={MAX_HOURS}
                step={1}
                className={COMPACT_NUMBER}
                aria-label={`${name} reminder: hours before each meeting`}
                aria-invalid={!!error}
                aria-describedby={error ? `${hoursId}-error` : undefined}
                value={draft.hours}
                onChange={(e) => prefs.setHours(channel, e.target.value)}
              />
              {draft.hours.trim() === "1" ? "hour" : "hours"} before
            </span>
            <input
              id={`${channel}-reminder-message`}
              maxLength={300}
              className={`${COMPACT_INPUT} w-full`}
              aria-label={`${name} reminder: note added to the end (optional)`}
              placeholder="Note added to the end (optional)"
              value={draft.message}
              onChange={(e) => prefs.setMessage(channel, e.target.value)}
            />
            {error && (
              <div className="col-span-2 col-start-2">
                <FieldError id={`${hoursId}-error`}>{error}</FieldError>
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * One row per message, with SMS and Email side by side; the reminder is
 * last, with its timing right under it while it is on. The weekly
 * pending-approval email to tutors is an organization setting, so its
 * value comes from the dialog.
 */
export default function NotificationSettings({
  prefs,
  pendingApprovalEmails,
  onPendingApprovalEmailsChange,
}: {
  prefs: NotificationPrefs;
  pendingApprovalEmails: boolean;
  onPendingApprovalEmailsChange: (on: boolean) => void;
}) {
  const { drafts, loadError } = prefs;
  const reminder = TYPES[TYPES.length - 1];
  const reminders = drafts
    ? CHANNELS.filter((channel) => drafts[channel].flags.MEETING_REMINDER)
    : [];

  return (
    <section className={`${SECTION} max-w-3xl`}>
      <h3 className={SECTION_TITLE}>Notifications</h3>
      <p className={SECTION_TEXT}>
        The messages Tadpole sends on its own, by SMS and by email.
      </p>

      <table className="mt-4 w-full table-fixed">
        <thead>
          <tr className={`border-b border-gray-200 text-left ${LABEL}`}>
            <th className="pb-2 pr-3">Message</th>
            <th className="w-20 pb-2 text-center">SMS</th>
            <th className="w-20 pb-2 text-center">Email</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {drafts ? (
            TYPES.slice(0, -1).map((type) => (
              <FlagRow
                key={type.key}
                type={type}
                drafts={drafts}
                prefs={prefs}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={3}
                className={`py-4 text-sm ${
                  loadError ? "text-red-600" : "text-gray-500"
                }`}
              >
                {loadError ?? "Loading…"}
              </td>
            </tr>
          )}
          <tr>
            <MessageCell
              name="Sessions waiting for approval"
              about="To each tutor on Saturday mornings, about their sessions still waiting for approval: by email, the list; by SMS, how many, with a link."
            />
            <td className="py-2.5 text-center">
              {drafts && (
                <CellSwitch
                  name="sms-pending-approval"
                  label="Sessions waiting for approval by SMS"
                  checked={!!drafts.sms.pendingApprovalTexts}
                  onChange={prefs.setPendingApprovalTexts}
                />
              )}
            </td>
            <td className="py-2.5 text-center">
              <CellSwitch
                name="email-pending-approval"
                label="Sessions waiting for approval by Email"
                checked={pendingApprovalEmails}
                onChange={onPendingApprovalEmailsChange}
              />
            </td>
          </tr>
          {drafts && <FlagRow type={reminder} drafts={drafts} prefs={prefs} />}
          {drafts && reminders.length > 0 && (
            // Part of the reminder's row: no line between them.
            <tr className="!border-t-0">
              <td colSpan={3}>
                <ReminderTiming
                  channels={reminders}
                  drafts={drafts}
                  prefs={prefs}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
